import { getCountryNameWithFlag, pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { useExistingPortfolioSnapshot } from "@/lib/hooks/use-existing-portfolio-snapshot";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import { getRelativeTime } from "@/lib/utils";
import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { EquityStyleGrid } from "../equity-style-grid";
import { XRayPieChart } from "../xray-pie-chart";
import { ComparisonLayout, ComparisonTable } from "./comparison-layout";
import { T } from "./portfolio-xray.i18n";
import type { PortfolioInfo } from "./types";

type PortfolioXrayProps = {
  portfolioId: string | null;
  config: {
    style?: string;
    title?: string;
    description?: string;
    displayMode?: string;
    showChart?: boolean;
    showTable?: boolean;
  };
  language: string;
  chartColors?: string[] | null;
  portfolioInfo: PortfolioInfo;
  /**
   * Phase 2 PR-A (S2) — engine reported `kind: "snapshot"` for this component on this
   * portfolio. Wizard threads `componentHasError` (gated on `showValidationErrors`). When set
   * AND prerequisites are met but the xray snapshot row is missing, replace the
   * misleading "X-Ray analysis not available" fallback with a clear snapshot-missing
   * placeholder pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
  /**
   * QP-177 — show the existing-vs-modified comparison view. The renderer sets this to
   * `portfolio is a followup AND cfg.enableFollowupComparison === true`; when false the
   * existing-snapshot fetch is skipped entirely.
   */
  showFollowupComparison?: boolean;
  /** Agreement follow-ups label the comparison "Savings Plan". */
  comparisonVariant?: "portfolio" | "savingsPlan";
};

export function PortfolioXray({
  portfolioId,
  config,
  language,
  chartColors,
  portfolioInfo,
  hasValidationError = false,
  showFollowupComparison = false,
  comparisonVariant = "portfolio",
}: PortfolioXrayProps) {
  const t = pickT(T, language);
  const xray = usePortfolioSnapshot("xray", portfolioId);
  const portfolio = usePortfolioSnapshot("portfolio", portfolioId);
  const existingXray = useExistingPortfolioSnapshot("xray", portfolioId, showFollowupComparison);

  const displayMode = config.displayMode || "asset_allocation";
  const showChart = config.showChart !== false;
  const showTable = config.showTable || false;

  if (xray.isLoading || portfolio.isLoading) return null;

  if (!xray.data) {
    if (!portfolio.data) {
      // QL-311 — empty-state contract order: custom first. A custom portfolio
      // is driven by the builder, so the risk prompt below must not fire for
      // it — Asset Allocation and Portfolio Display diagnose this state as
      // "no products yet" and this widget has to agree.
      if (portfolioInfo.isCustomPortfolio) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.noProductsXrayPending}
          </div>
        );
      }
      if (!portfolioInfo.hasRiskAssessment) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.completeRiskOrBuildCustom}
          </div>
        );
      }
      // QL-311 — a missing amount blocks snapshot generation too; without this
      // branch it fell through to the no-portfolio-for-score message, blaming
      // collection configuration for a missing user input.
      if (!portfolioInfo.hasInvestmentAmount) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.enterInvestmentAmount}
          </div>
        );
      }
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.noPortfolioForScore(String(portfolioInfo.riskScore ?? ""))}
        </div>
      );
    }
    // PR-A (S2) — engine D20 N/A guard means the prerequisite branches above wouldn't
    // trigger `hasValidationError`. Reaching this fallback AND hasValidationError=true
    // means prerequisites are met but the xray snapshot row is missing (system-bug case);
    // replace the misleading "ISIN-less products" hint with a clear regen pointer.
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.xrayNotAvailable}
      </div>
    );
  }

  const snapshot =
    typeof xray.data === "string"
      ? (() => {
          try {
            return JSON.parse(xray.data as string);
          } catch {
            return null;
          }
        })()
      : (xray.data as Record<string, any>);

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoadingXray}</div>;
  }

  if (snapshot.error) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        <div className="font-medium mb-1">{t.xrayUnavailable}</div>
        <div>{String(snapshot.error)}</div>
      </div>
    );
  }

  // Transform data for chart components
  const assetAllocationData = Array.isArray(snapshot.assetAllocation)
    ? snapshot.assetAllocation.map((item: any) => ({
        id: item.assetClass,
        // QL-304 phase 1 — the snapshot stores the Unknown bucket as a
        // sentinel; only the display label is translated (the id stays
        // stable so comparison merging and colors work).
        label: item.assetClass === "Unknown" ? t.unknownBucket : item.assetClass,
        value: item.percentage,
      }))
    : [];

  const countryExposureData = Array.isArray(snapshot.countryExposure)
    ? snapshot.countryExposure
        .sort((a: any, b: any) => b.percentage - a.percentage)
        .slice(0, 10)
        .map((item: any) => ({
          id: item.countryCode || item.country,
          label: getCountryNameWithFlag(item.countryCode || item.country, language),
          value: item.percentage,
        }))
    : [];

  const regionalExposureData = Array.isArray(snapshot.regionalExposure)
    ? snapshot.regionalExposure
        .sort((a: any, b: any) => b.percentage - a.percentage)
        .map((item: any) => ({ id: item.region, label: item.region, value: item.percentage }))
    : [];

  const stockSectorData = Array.isArray(snapshot.globalStockSector)
    ? snapshot.globalStockSector.map((item: any) => ({
        id: item.sector,
        label: item.sector,
        value: item.percentage,
      }))
    : [];

  const styleBoxData = Array.isArray(snapshot.styleBox) ? snapshot.styleBox : [];

  const bondSectorData = Array.isArray(snapshot.globalBondSector)
    ? snapshot.globalBondSector.map((item: any) => ({
        id: item.sector,
        label: item.sector,
        value: Math.abs(item.percentage),
      }))
    : [];

  const renderContent = () => {
    const commonProps = {
      timestamp: snapshot.timestamp as string,
      showChart,
      showTable,
      chartColors,
      language,
      coverage: snapshot.coverage as any,
    };

    switch (displayMode) {
      case "asset_allocation":
        return assetAllocationData.length > 0 ? (
          <XRayPieChart
            data={assetAllocationData}
            categoryLabel={t.categoryAssetClass}
            {...commonProps}
          />
        ) : null;
      case "country_exposure":
        return countryExposureData.length > 0 ? (
          <XRayPieChart
            data={countryExposureData}
            categoryLabel={t.categoryCountry}
            {...commonProps}
          />
        ) : null;
      case "regional_exposure":
        return regionalExposureData.length > 0 ? (
          <XRayPieChart
            data={regionalExposureData}
            categoryLabel={t.categoryRegion}
            {...commonProps}
          />
        ) : null;
      case "stock_sector":
        return stockSectorData.length > 0 ? (
          <XRayPieChart data={stockSectorData} categoryLabel={t.categorySector} {...commonProps} />
        ) : null;
      case "equity_style":
        return styleBoxData.length > 0 ? (
          <EquityStyleGrid
            data={styleBoxData}
            timestamp={snapshot.timestamp as string}
            language={language}
            coverage={snapshot.coverage as any}
          />
        ) : null;
      case "bond_sector":
        return bondSectorData.length > 0 ? (
          <XRayPieChart data={bondSectorData} categoryLabel={t.categorySector} {...commonProps} />
        ) : null;
      case "top_holdings":
        if (!Array.isArray(snapshot.topHoldings) || snapshot.topHoldings.length === 0) return null;
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              {(snapshot.topHoldings as any[]).slice(0, 10).map((holding: any) => (
                <div key={holding.name} className="border-b pb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{holding.name}</span>
                    <span className="text-sm font-semibold">{holding.weight.toFixed(2)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                    <span>{getCountryNameWithFlag(holding.country, language)}</span>
                    <span>{holding.sector && holding.sector !== "N/A" ? holding.sector : ""}</span>
                  </div>
                </div>
              ))}
            </div>
            {typeof snapshot.timestamp === "string" && (
              <div className="text-xs text-muted-foreground pt-2">
                {t.snapshotTaken(getRelativeTime(snapshot.timestamp, language))}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const content = renderContent();
  if (!content) {
    return <div className="text-sm text-muted-foreground">{t.noDataForView}</div>;
  }

  // QP-177 — followup comparison: show side-by-side with existing portfolio
  if (showFollowupComparison && existingXray.data && !existingXray.isLoading) {
    const existingSnapshot =
      typeof existingXray.data === "string"
        ? (() => {
            try {
              return JSON.parse(existingXray.data as string);
            } catch {
              return null;
            }
          })()
        : (existingXray.data as Record<string, any>);

    if (existingSnapshot && !existingSnapshot.error) {
      // Build chart data for the active display mode from existing snapshot
      let existingChartData: Array<{ id: string; label: string; value: number }> = [];
      let modifiedChartData: Array<{ id: string; label: string; value: number }> = [];
      let categoryLabel = t.categoryAssetClass;

      switch (displayMode) {
        case "asset_allocation":
          existingChartData = Array.isArray(existingSnapshot.assetAllocation)
            ? existingSnapshot.assetAllocation.map((item: any) => ({
                id: item.assetClass,
                label: item.assetClass === "Unknown" ? t.unknownBucket : item.assetClass,
                value: item.percentage,
              }))
            : [];
          modifiedChartData = assetAllocationData;
          categoryLabel = t.categoryAssetClass;
          break;
        case "regional_exposure":
          existingChartData = Array.isArray(existingSnapshot.regionalExposure)
            ? existingSnapshot.regionalExposure.map((item: any) => ({
                id: item.region,
                label: item.region,
                value: item.percentage,
              }))
            : [];
          modifiedChartData = regionalExposureData;
          categoryLabel = t.categoryRegion;
          break;
        case "stock_sector":
          existingChartData = Array.isArray(existingSnapshot.globalStockSector)
            ? existingSnapshot.globalStockSector.map((item: any) => ({
                id: item.sector,
                label: item.sector,
                value: item.percentage,
              }))
            : [];
          modifiedChartData = stockSectorData;
          categoryLabel = t.categorySector;
          break;
        case "country_exposure":
          existingChartData = Array.isArray(existingSnapshot.countryExposure)
            ? existingSnapshot.countryExposure
                .sort((a: any, b: any) => b.percentage - a.percentage)
                .slice(0, 10)
                .map((item: any) => ({
                  id: item.countryCode || item.country,
                  label: getCountryNameWithFlag(item.countryCode || item.country, language),
                  value: item.percentage,
                }))
            : [];
          modifiedChartData = countryExposureData;
          categoryLabel = t.categoryCountry;
          break;
        case "bond_sector":
          existingChartData = Array.isArray(existingSnapshot.globalBondSector)
            ? existingSnapshot.globalBondSector.map((item: any) => ({
                id: item.sector,
                label: item.sector,
                value: Math.abs(item.percentage),
              }))
            : [];
          modifiedChartData = bondSectorData;
          categoryLabel = t.categorySector;
          break;
        case "equity_style": {
          // Grid, not pie — early return with its own layout: one style grid per side
          // plus the merged Δ table over the 9 Morningstar style-box cells.
          const existingStyleBox: Array<{ style: string; percentage: number }> = Array.isArray(
            existingSnapshot.styleBox,
          )
            ? existingSnapshot.styleBox
            : [];
          if (existingStyleBox.length === 0) {
            return <VisualizationContainer config={config}>{content}</VisualizationContainer>;
          }

          // Morningstar style codes 1-9, row-major: size = rows (Large/Mid/Small),
          // style = columns (Value/Blend/Growth).
          const sizeLabels = [t.sizeLarge, t.sizeMid, t.sizeSmall];
          const styleLabels = [t.styleValue, t.styleBlend, t.styleGrowth];
          const styleCodeLabel = (code: string) => {
            const n = Number(code);
            if (!Number.isInteger(n) || n < 1 || n > 9) return code;
            return `${sizeLabels[Math.floor((n - 1) / 3)]} ${styleLabels[(n - 1) % 3]}`;
          };
          // Drop zero-weight codes per side — a cell zero on BOTH sides disappears
          // from the merged table, while a one-sided zero stays as a real change.
          const toStyleRows = (items: Array<{ style: string; percentage: number }>) =>
            items
              .filter((item) => item.percentage > 0)
              .map((item) => ({
                id: item.style,
                label: styleCodeLabel(item.style),
                value: item.percentage,
              }));

          return (
            <VisualizationContainer config={config}>
              <ComparisonLayout
                variant={comparisonVariant}
                existingContent={
                  <EquityStyleGrid
                    data={existingStyleBox}
                    language={language}
                    showLegend={false}
                    coverage={existingSnapshot.coverage}
                  />
                }
                modifiedContent={
                  <EquityStyleGrid
                    data={styleBoxData}
                    language={language}
                    showLegend={false}
                    coverage={snapshot.coverage as any}
                  />
                }
              />
              <ComparisonTable
                variant={comparisonVariant}
                existingData={toStyleRows(existingStyleBox)}
                modifiedData={toStyleRows(styleBoxData)}
                categoryLabel={t.categoryStyle}
              />
            </VisualizationContainer>
          );
        }
        default:
          // For modes without comparison support, show single view
          return <VisualizationContainer config={config}>{content}</VisualizationContainer>;
      }

      if (existingChartData.length > 0) {
        // One color per category across BOTH pies and the comparison table — each
        // XRayPieChart would otherwise color by its own sort order, giving the same
        // category different colors on the two sides.
        const palette = chartColors || TABLEAU20_COLORS;
        const colorById = new Map<string, string>();
        // The Unknown bucket is always muted gray, never a palette color.
        colorById.set("Unknown", "#9ca3af");
        for (const item of [
          ...[...modifiedChartData].sort((a, b) => b.value - a.value),
          ...[...existingChartData].sort((a, b) => b.value - a.value),
        ]) {
          if (!colorById.has(item.id)) {
            colorById.set(item.id, palette[colorById.size % palette.length]);
          }
        }
        const existingColored = existingChartData.map((d) => ({
          ...d,
          color: colorById.get(d.id),
        }));
        const modifiedColored = modifiedChartData.map((d) => ({
          ...d,
          color: colorById.get(d.id),
        }));

        const comparisonCommonProps = {
          showChart,
          showTable: false,
          chartColors,
          language,
          height: 220,
        };

        return (
          <VisualizationContainer config={config}>
            <ComparisonLayout
              variant={comparisonVariant}
              existingContent={
                <XRayPieChart
                  data={existingColored}
                  categoryLabel={categoryLabel}
                  coverage={existingSnapshot.coverage}
                  {...comparisonCommonProps}
                />
              }
              modifiedContent={
                <XRayPieChart
                  data={modifiedColored}
                  categoryLabel={categoryLabel}
                  coverage={snapshot.coverage as any}
                  {...comparisonCommonProps}
                />
              }
            />
            <ComparisonTable
              variant={comparisonVariant}
              existingData={existingColored}
              modifiedData={modifiedColored}
              categoryLabel={categoryLabel}
            />
          </VisualizationContainer>
        );
      }
    }
  }

  return <VisualizationContainer config={config}>{content}</VisualizationContainer>;
}
